import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DefaultOutlinedButton, DefaultContainedButton } from './Buttons';
import { CustomAutocomplete, CustomDatePicker, CustomTextField } from './StyledInputs';

import { LoadingUI } from './LayoutStyles';
import { HttpErrorHandler } from './ErrorHandler';
import { EntitiesDialog } from './EntitiesDialog';
import API from '../api';
import { usePopup } from '../context/PopupContext';

dayjs.locale('it');

export const NewPositionDialog = ({ openDialog, closeDialog, userAuth, userInfo, refreshFunction }) => {
  const { showPopup, setPopUpText } = usePopup();

  const [countriesList, setCountriesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [apiCalled, setApiCalled] = useState(false);

  const [newPosition, setNewPosition] = useState(null);
  const [newPositionType, setNewPositionType] = useState('');
  const [newPositionStartDate, setNewPositionStartDate] = useState('');
  const [newPositionEndDate, setNewPositionEndDate] = useState('');
  const [newPositionEmail, setNewPositionEmail] = useState('');

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [positionsList, setPositionsList] = useState([]);

  // Get Countries and Entity Types
  useEffect(() => {
    if (openDialog) {
      API.get(`/countries`).then((response) => {
        setCountriesList(response.data);
      });

      if (userAuth === 'admin') {
        API.get('/positions').then((response) => {
          setPositionsList(response.data);
        });
      } else if (userAuth === 'user') {
        API.get('institutional_positions').then((response) => {
          setPositionsList(response.data.institutional_positions);
        });
      }
    }
  }, [openDialog]);

  // Set Loading Status
  useEffect(() => {
    if (countriesList.length > 0 && positionsList.length > 0) {
      setApiCalled(true);
    }
  }, [countriesList, positionsList]);

  //   Get Italy Default Country
  useEffect(() => {
    const italy = countriesList.find((element) => element.name === 'Italia');
    setSelectedCountry(italy);
  }, [countriesList]);

  //   Get Regions List
  useEffect(() => {
    if (selectedCountry !== null && selectedCountry !== undefined && selectedCountry !== '') {
      if (typeof selectedCountry === 'object' && Object.keys(selectedCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: selectedCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionsList(response.data);
          } else {
            setRegionsList([]);
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (selectedRegion !== null && selectedRegion !== undefined && selectedRegion !== '') {
      if (typeof selectedRegion === 'object' && Object.keys(selectedRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: selectedRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedRegion]);

  //   Get Cities List
  useEffect(() => {
    if (selectedProvince !== null && selectedProvince !== undefined && selectedProvince !== '') {
      if (typeof selectedProvince === 'object' && Object.keys(selectedProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: selectedProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedProvince]);

  const emptyField = () => {
    closeDialog();
    setSelectedProvince(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setNewPosition({});
    setNewPositionEmail('');
    setNewPositionStartDate('');
    setNewPositionEndDate('');
    setNewPositionType('');
    setFilteredEntity(null);
    setProvincesList([]);
    setCitiesList([]);
  };

  useEffect(() => {
    if (openDialog && newPosition !== null && userAuth === 'admin') {
      if (newPosition.internal === 1) {
        setNewPositionType('internal');
      } else {
        setNewPositionType('external');
      }
    }
  }, [newPosition]);

  // Get NewPosition Start Date
  const handleNewPositionStartDate = (value) => {
    setNewPositionStartDate(value);
  };
  // Get NewPosition End Date
  const handleNewPositionEndDate = (value) => {
    setNewPositionEndDate(value);
  };
  // Get NewPosition Email
  const handleNewPositionEmail = (e) => {
    setNewPositionEmail(e.currentTarget.value);
  };

  const submitNewPosition = (e) => {
    e.preventDefault();

    if (userAuth === 'user') {
      const newPositionData = {
        user_id: userInfo.id,
        position_id: newPosition.id,
        country_id: selectedCountry ? selectedCountry.id : '',
        region_name: selectedRegion ? selectedRegion.region_name : '',
        province_id: selectedProvince ? selectedProvince.id : '',
        city_id: selectedCity ? selectedCity.id : '',
        start_date: dayjs(newPositionStartDate).format('YYYY/MM/DD'),
      };

      let itemsLeft = false;

      Object.keys(newPositionData).forEach((key) => {
        const value = newPositionData[key];

        if (Object.prototype.hasOwnProperty.call(newPositionData, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)
          ) {
            itemsLeft = true;
            showPopup('warning', 'Inserisci i dati mancanti');
          }
        }
      });

      if (!itemsLeft) {
        delete newPositionData.region_name;

        if (
          newPositionEndDate !== '' &&
          newPositionEndDate !== 'Invalid Date' &&
          newPositionEndDate !== null &&
          newPositionEndDate !== undefined
        ) {
          newPositionData.end_date = dayjs(newPositionEndDate).format('YYYY/MM/DD');
        }

        if (
          newPositionEmail !== '' &&
          newPositionEmail !== null &&
          newPositionEmail !== undefined &&
          newPositionEmail.length > 0
        ) {
          newPositionData.email = newPositionEmail;
        }

        if (newPositionData.end_date) {
          if (dayjs(newPositionData.end_date).isAfter(dayjs(newPositionData.start_date))) {
            API.post('/charges', newPositionData)
              .then((response) => {
                closeDialog();
                refreshFunction();
              })
              .catch((error) => {
                HttpErrorHandler(error, setPopUpText);
                showPopup(false);
              });
          } else {
            showPopup(false, 'La data di fine non può essere antecedente a quella di inizio');
          }
        } else {
          API.post('/charges', newPositionData)
            .then((response) => {
              closeDialog();
              refreshFunction();
            })
            .catch((error) => {
              HttpErrorHandler(error, setPopUpText);
              showPopup(false);
            });
        }
      }
    } else {
      const newPositionData = {
        user_id: userInfo.id,
        position_id: newPosition.id,
        start_date: dayjs(newPositionStartDate).format('YYYY/MM/DD'),
      };

      if (newPositionType === 'internal') {
        newPositionData.entity_id = filteredEntity.id;
      }

      if (newPositionType === 'external') {
        newPositionData.country_id = selectedCountry ? selectedCountry.id : '';
        newPositionData.region_name = selectedRegion ? selectedRegion.region_name : '';
        newPositionData.province_id = selectedProvince ? selectedProvince.id : '';
        newPositionData.city_id = selectedCity ? selectedCity.id : '';
      }

      let itemsLeft = false;

      Object.keys(newPositionData).forEach((key) => {
        const value = newPositionData[key];
        if (Object.prototype.hasOwnProperty.call(newPositionData, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)
          ) {
            itemsLeft = true;
            showPopup('warning', 'Inserisci i dati mancanti');
          }
        }
      });

      if (!itemsLeft) {
        if (newPositionType === 'external') {
          delete newPositionData.region_name;
        }

        if (
          newPositionEndDate !== '' &&
          newPositionEndDate !== 'Invalid Date' &&
          newPositionEndDate !== null &&
          newPositionEndDate !== undefined
        ) {
          newPositionData.end_date = dayjs(newPositionEndDate).format('YYYY/MM/DD');
        }

        if (
          newPositionEmail !== '' &&
          newPositionEmail !== null &&
          newPositionEmail !== undefined &&
          newPositionEmail.length > 0
        ) {
          newPositionData.email = newPositionEmail;
        }

        if (newPositionData.end_date) {
          if (dayjs(newPositionData.end_date).isAfter(dayjs(newPositionData.start_date))) {
            API.post('/charges', newPositionData)
              .then((response) => {
                refreshFunction();
                emptyField();
              })
              .catch((error) => {
                HttpErrorHandler(error, setPopUpText);
                showPopup(false);
              });
          } else {
            showPopup(false, 'La data di fine non può essere antecendete a quella di inizio');
          }
        } else {
          API.post('/charges', newPositionData)
            .then((response) => {
              refreshFunction();
              emptyField();
            })
            .catch((error) => {
              HttpErrorHandler(error, setPopUpText);
              showPopup(false);
            });
        }
      }
    }
  };

  /* ----------------------------- Entities Filter ---------------------------- */
  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);
  const [filteredEntity, setFilteredEntity] = useState(null);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  const getFilteredEntity = (filteredEntity) => {
    setFilteredEntity(filteredEntity);
  };


  return (
    <Dialog fullWidth open={openDialog} onClose={() => {}}>
      <DialogTitle sx={{ textAlign: 'center' }}>Aggiungi carica</DialogTitle>
      {apiCalled ? (
        <>
          <form onSubmit={submitNewPosition}>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Position */}
                <Grid item xs={12} sx={{ overflow: 'auto' }}>
                  <CustomAutocomplete
                    required
                    id="newPosition"
                    options={positionsList}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.name}
                      </Box>
                    )}
                    onChange={(e, value) => setNewPosition(value)}
                    renderInput={(params) => <TextField {...params} label="Carica *" variant="outlined" />}
                  />
                </Grid>

                {(userAuth === 'user' || (userAuth === 'admin' && newPositionType === 'external')) && (
                  <>
                    {/* Country */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        readOnly
                        forcePopupIcon={false}
                        options={countriesList.filter(
                          (country) =>
                            (!selectedRegion || country.id === selectedRegion.country_id) &&
                            (!selectedProvince || country.id === selectedProvince.country_id)
                        )}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedCountry}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        onChange={(e, value) => setSelectedCountry(value)}
                        renderInput={(params) => <TextField {...params} label="Paese" variant="outlined" />}
                      />
                    </Grid>

                    {/* Region */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        options={regionsList.filter(
                          (region) =>
                            (!selectedProvince || region.region_name === selectedProvince.region_name) &&
                            (!selectedCountry || region.country_id === selectedCountry.id)
                        )}
                        isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                        getOptionLabel={(option) => option.region_name}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.region_name}>
                            {option.region_name}
                          </Box>
                        )}
                        onChange={(e, value) => setSelectedRegion(value)}
                        renderInput={(params) => <TextField {...params} label="Regione *" variant="outlined" />}
                      />
                    </Grid>

                    {/* Province */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        options={provincesList.filter(
                          (province) =>
                            (!selectedRegion || province.region_name === selectedRegion.region_name) &&
                            (!selectedCity || province.id === selectedCity.province_id) &&
                            (!selectedCountry || province.country_id === selectedCountry.id)
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        onChange={(e, value) => setSelectedProvince(value)}
                        renderInput={(params) => <TextField {...params} label="Provincia *" variant="outlined" />}
                      />
                    </Grid>

                    {/* City */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        options={citiesList.filter(
                          (city) => !selectedProvince || city.province_id === selectedProvince.id
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        onChange={(e, value) => setSelectedCity(value)}
                        renderInput={(params) => <TextField {...params} label="Comune *" variant="outlined" />}
                      />
                    </Grid>
                  </>
                )}

                {userAuth === 'admin' && newPositionType === 'internal' && (
                  <Grid item xs={12}>
                    {!filteredEntity ? (
                      <DefaultOutlinedButton
                        customStyles={{ width: '100%' }}
                        onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                      >
                        Cerca Entità
                      </DefaultOutlinedButton>
                    ) : (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <CustomTextField
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                  <Tooltip title="Modifica" placement="left" arrow>
                                    <EditIcon onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)} />
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                            value={filteredEntity.name}
                            label="Entità"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                )}

                <EntitiesDialog
                  openDialog={openEntitiesFilter}
                  closeDialog={handleCloseEntitiesFilterDialog}
                  outputEntity={getFilteredEntity}
                  context={'userPosition'}
                />

                {/* Start Date */}
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                    <CustomDatePicker
                      required
                      label="Data Inizio Carica *"
                      onChange={handleNewPositionStartDate}
                      maxDate={dayjs(newPositionEndDate)}
                    />
                  </LocalizationProvider>
                </Grid>
                {/* End Date */}
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                    <CustomDatePicker
                      label="Data Fine Carica"
                      onChange={handleNewPositionEndDate}
                      minDate={dayjs(newPositionStartDate)}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Institutional Email */}
                {(userAuth === 'user' || (userAuth === 'admin' && newPositionType === 'external')) && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ marginBottom: '8px' }}>
                      Attenzione! Inserire esclusivamente una email istituzionale, questa sarà visible all'interno
                      dell'organizzazione.
                    </Alert>
                    <CustomTextField onChange={handleNewPositionEmail} label="Email Istituzionale" />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <DefaultOutlinedButton onClick={() => emptyField()}>Chiudi</DefaultOutlinedButton>
              <DefaultContainedButton type="submit">Aggiungi</DefaultContainedButton>

            </DialogActions>
          </form>
        </>
      ) : (
        LoadingUI()
      )}
      {!apiCalled && (
        <DialogActions>
          <DefaultOutlinedButton onClick={() => emptyField()}>Chiudi</DefaultOutlinedButton>
        </DialogActions>
      )}
    </Dialog>
  );
};
